/**
 * AuthContext - Manages authentication state for the new RBAC system.
 *
 * Provides:
 *   - user object (id, name, email, role, markets, accountTier, etc.)
 *   - isAuthenticated boolean
 *   - isLoading boolean (true during initial token validation)
 *   - login(email, password) function
 *   - logout() function
 *   - refreshUser() function (re-fetches user data from backend)
 *
 * On mount: checks localStorage for existing token, validates via GET /api/auth/me,
 * sets auth state accordingly. If token is expired/invalid, clears silently.
 *
 * This context is used for OTC's email/password auth. TCC continues to use
 * the legacy shared-code system in utils/auth.js.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const TOKEN_KEY = 'hh_auth_token';
const USER_KEY = 'hh_auth_user';

// Backend API base URL
const API_BASE = process.env.REACT_APP_API_URL || '';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Validate the stored token on mount.
   */
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
        } else {
          // Token is invalid/expired — clear it
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      } catch (err) {
        console.error('Token validation failed:', err);
        // Network error — keep token but don't set user
        // Try to use cached user data
        try {
          const cachedUser = JSON.parse(localStorage.getItem(USER_KEY));
          if (cachedUser) {
            setUser(cachedUser);
          }
        } catch (e) {
          // No cached user
        }
      }

      setIsLoading(false);
    };

    validateToken();
  }, []);

  /**
   * Login with email and password.
   * @returns {{ success: boolean, user?: Object, error?: string, passwordMustChange?: boolean }}
   */
  const login = useCallback(async (email, password, marketId) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, market: marketId || process.env.REACT_APP_MARKET || '' }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.error || 'Login failed' };
      }

      // Store token and user
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);

      return {
        success: true,
        user: data.user,
        passwordMustChange: data.user.passwordMustChange || false,
      };

    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'Unable to connect to server' };
    }
  }, []);

  /**
   * Logout — clear token and user state.
   */
  const logout = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    // Clear local state immediately
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);

    // Tell the backend to invalidate the session (fire and forget)
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (err) {
        // Logout should always succeed locally even if backend call fails
      }
    }
  }, []);

  /**
   * Refresh user data from backend (e.g., after password change).
   */
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      }
    } catch (err) {
      console.error('Refresh user failed:', err);
    }
  }, []);

  /**
   * Get the stored auth token for API requests.
   */
  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context.
 *
 * Usage:
 *   const { user, login, logout, isAuthenticated } = useAuth();
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
